import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase, TABLES, SafeIcon } from '../../lib/core';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiEdit3, FiTrash2, FiAlertTriangle, FiActivity, FiClock, FiShield, FiSearch, FiDollarSign, FiMessageSquare } = FiIcons;

const MasterActivity = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('approvals');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal States
  const [adjustModal, setAdjustModal] = useState({ open: false, log: null, price: '', reason: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, log: null, reason: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => { if (user) fetchLogs(); }, [user, activeView]);

  const fetchLogs = async () => {
    setLoading(true);
    let query = supabase
      .from(TABLES.LOGS)
      .select(`*, pilot:pilot_id(username, nickname), item:item_id(name, category), char:character_id(name)`)
      .eq('master_id', user.id)
      .order('created_at', { ascending: false });

    if (activeView === 'approvals') {
      query = query.in('status', ['pending_master', 'contested']);
    } else if (activeView === 'review') {
      query = query.in('status', ['pending_pilot', 'pending_delete']);
    } else {
      query = query.in('status', ['unpaid', 'paid']);
    }

    const { data } = await query;
    setLogs(data || []);
    setLoading(false);
  };

  const executeAdjust = async () => {
    if (!adjustModal.price || !adjustModal.reason) return alert("Fill all fields");
    setProcessing(true);
    const cleanPrice = parseFloat(String(adjustModal.price).replace(/,/g, ''));
    
    try {
      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({
          status: 'pending_pilot',
          value_at_log: cleanPrice,
          master_adjustment_price: cleanPrice,
          master_adjustment_reason: adjustModal.reason
        })
        .eq('id', adjustModal.log.id);

      if (error) throw error;
      setAdjustModal({ open: false, log: null, price: '', reason: '' });
      fetchLogs();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const executeDeleteRequest = async () => {
    if (!deleteModal.reason) return alert("Reason required");
    setProcessing(true);
    try {
      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({
          status: 'pending_delete',
          delete_reason: deleteModal.reason
        })
        .eq('id', deleteModal.log.id);

      if (error) throw error;
      setDeleteModal({ open: false, log: null, reason: '' });
      fetchLogs();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async (logId) => {
    setProcessing(true);
    try {
      const { error } = await supabase.from(TABLES.LOGS).update({ status: 'unpaid' }).eq('id', logId);
      if (error) throw error;
      fetchLogs();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-12">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight">Master Ledger</h2>
          <p className="text-gray-400 font-bold mt-2 italic">Review and manage drops.</p>
        </div>
        <div className="flex bg-gray-200/50 p-1.5 rounded-[1.5rem] backdrop-blur-md border border-white">
          {['approvals', 'review', 'history'].map(view => (
            <button key={view} onClick={() => setActiveView(view)} className={`px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${activeView === view ? 'bg-white shadow-xl text-blue-600' : 'text-gray-400'}`}>
              {view}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {logs.map(log => (
          <div key={log.id} className="bento-card border-l-4 border-l-blue-500">
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-xl relative overflow-hidden">
                  {log.proof_url ? <img src={log.proof_url} className="w-full h-full object-cover" /> : <FiActivity />}
                </div>
                <div>
                  <h4 className="font-black text-xl text-gray-900">{log.item?.name}</h4>
                  <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">@{log.pilot?.nickname || log.pilot?.username}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-gray-900">₱{log.value_at_log?.toLocaleString()}</p>
              </div>
            </div>

            {log.status === 'pending_delete' && (
              <div className="mb-6 p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-center gap-3">
                <FiShield className="text-amber-600" />
                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Sent for Admin Approval</p>
              </div>
            )}

            {activeView === 'approvals' && (
              <div className="flex gap-3">
                <button onClick={() => handleApprove(log.id)} className="flex-1 apple-button-primary !py-4 text-[10px] uppercase tracking-widest !bg-emerald-600">Approve</button>
                <button onClick={() => setAdjustModal({ open: true, log, price: log.value_at_log, reason: '' })} className="flex-1 apple-button-secondary !py-4 text-[10px] uppercase tracking-widest">Adjust</button>
                <button onClick={() => setDeleteModal({ open: true, log, reason: '' })} className="p-4 bg-red-50 text-red-500 rounded-2xl hover:bg-red-500 hover:text-white transition-all"><FiTrash2 /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Adjust Modal */}
      <AnimatePresence>
        {adjustModal.open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-md bg-white rounded-[2.5rem] p-8 shadow-2xl">
              <h3 className="text-2xl font-black mb-6">Adjust Pricing</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2">New Price (₱)</label>
                  <div className="relative">
                    <FiDollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="number" className="apple-input !pl-10" value={adjustModal.price} onChange={e => setAdjustModal({...adjustModal, price: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Reason</label>
                  <textarea className="apple-input !h-24 resize-none" value={adjustModal.reason} onChange={e => setAdjustModal({...adjustModal, reason: e.target.value})} placeholder="e.g. Market price drop" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setAdjustModal({ open: false, log: null, price: '', reason: '' })} className="flex-1 py-4 bg-gray-100 rounded-2xl font-black text-[10px] uppercase">Cancel</button>
                  <button onClick={executeAdjust} disabled={processing} className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase shadow-lg">Confirm Adjust</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {deleteModal.open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-md bg-white rounded-[2.5rem] p-8 shadow-2xl">
              <h3 className="text-2xl font-black mb-2 text-red-600">Request Deletion</h3>
              <p className="text-sm text-gray-500 mb-6 font-bold">This requires Super Admin approval.</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Reason for Deletion</label>
                  <textarea className="apple-input !h-24 resize-none" value={deleteModal.reason} onChange={e => setDeleteModal({...deleteModal, reason: e.target.value})} placeholder="e.g. Duplicate entry" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setDeleteModal({ open: false, log: null, reason: '' })} className="flex-1 py-4 bg-gray-100 rounded-2xl font-black text-[10px] uppercase">Cancel</button>
                  <button onClick={executeDeleteRequest} disabled={processing} className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase shadow-lg">Request Deletion</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MasterActivity;